/**
 * Error classification helpers for the Reviews feature.
 *
 * safeFetchAPI in src/api/index.ts collapses non-2xx responses into a single
 * string. These helpers pattern-match on the BE error copy so screens can
 * distinguish "reviews are disabled" (show fallback UI) from "contact not
 * found" (send back to sign-in) from "daily review limit reached" (surface
 * a friendly toast) without a shared status-code channel.
 *
 * Keep the regexes tolerant so BE copy tweaks do not silently break the UI.
 */

export type ReviewErrorClass =
  | 'reviewsDisabled'
  | 'contactNotFound'
  | 'dailyLimitReached'
  | 'notPurchased'
  | 'invalidRating'
  | 'invalidPotency'
  | 'unknown'

export function classifyReviewError(error: string | undefined | null): ReviewErrorClass {
  if (!error) return 'unknown'
  if (/not enabled|reviews.*disabled|forbidden|not available/i.test(error)) return 'reviewsDisabled'
  if (/contact not found|no contact/i.test(error)) return 'contactNotFound'
  if (/daily review limit/i.test(error)) return 'dailyLimitReached'
  if (/not purchased|purchase.*window/i.test(error)) return 'notPurchased'
  if (/rating.*between|rating.*1.*5/i.test(error)) return 'invalidRating'
  if (/potency.*between|potency.*1.*5/i.test(error)) return 'invalidPotency'
  return 'unknown'
}

const DEFAULT_COPY: Record<ReviewErrorClass, string> = {
  reviewsDisabled: 'Reviews are not available for this store.',
  contactNotFound: 'We could not find your account. Please sign in again.',
  dailyLimitReached: "You've reached your daily review limit. Please try again tomorrow.",
  notPurchased: 'This product is no longer eligible for a review.',
  invalidRating: 'Please rate the product before submitting.',
  invalidPotency: 'Please select a potency before submitting.',
  unknown: 'Something went wrong. Please try again.',
}

export function reviewErrorMessage(error: string | undefined | null, fallback?: string): string {
  const cls = classifyReviewError(error)
  if (cls === 'unknown') return error || fallback || DEFAULT_COPY.unknown
  return DEFAULT_COPY[cls]
}
